<template>
  <div>
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="[
          { title: 'Child Development', href: '/child-development', color: 'primary' },
          { title: `Class`, href: childClassId ? `/child-development/class/${year}/${childClassId}` : undefined, color: 'primary', disabled: !childClassId },
          { title: `${childName} (${year} ${$t('childDevelopment.fiscalYear')})` },
        ]"
      ></v-breadcrumbs>
    </div>
    <page-title
      :value="`${$t('childDevelopment.progressRecord.title')} - ${childName} (${year != null ? year : ''} ${$t('common.schoolYear')})`"
    />
    <v-alert
      class="my-4"
      type="info"
      color="primary"
      variant="outlined"
      density="compact"
      style="font-size: 0.9rem"
    >
      <strong>{{ $t('childDevelopment.progressRecord.instructions.title') }}</strong><br/>
      • {{ $t('childDevelopment.progressRecord.instructions.interest') }}<br/>
      • {{ $t('childDevelopment.progressRecord.instructions.growth') }}<br/>
      • {{ $t('childDevelopment.progressRecord.instructions.play') }}<br/>
      • {{ $t('childDevelopment.progressRecord.instructions.perspective') }}<br/>

    </v-alert>
    <v-progress-linear v-if="isFetching" indeterminate color="primary" class="my-4" />
    <div v-else class="ma-2">
      <v-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left" style="width: 100px;">{{ $t('childDevelopment.progressRecord.month') }}</th>
              <th class="text-left">{{ $t('childDevelopment.progressRecord.content') }}</th>
              <th class="text-left" style="width: 100px;">{{ $t('childDevelopment.progressRecord.edit') }}</th>
              <th class="text-left" style="width: 180px;">{{ $t('childDevelopment.progressRecord.updatedDate') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in progressRecords" :key="record.month">
              <td>{{ getMonthLabel(record.month) }}</td>
              <td class="content-cell">{{ record.content || '-' }}</td>
              <td>
                <v-btn
                  small
                  variant="flat"
                  color="primary"
                  @click="openEditDialog(record)"
                >
                  {{ $t('childDevelopment.progressRecord.edit') }}
                </v-btn>
              </td>
              <td>{{ formatDate(record.updatedAt) }}</td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </div>

    <progress-record-edit-dialog
      :is-open="isDialogOpen"
      :month="editingMonth"
      :content="editingContent"
      :is-saving="isSaving"
      @is-open="isDialogOpen = $event"
      @save="saveProgressRecord"
    />
  </div>
</template>
<script lang="ts">
import dayjs from 'dayjs';
import { defineComponent } from 'vue';
import PageTitle from '@/components/atoms/PageTitle.vue';
import ProgressRecordEditDialog from '@/components/molecules/ProgressRecordEditDialog.vue';

import { childDevelopmentChildModule } from '@/store/viewModules/child-development/childDevelopmentChildModule';

interface ProgressRecord {
  month: number;
  content: string | null;
  updatedAt: string | null;
}

interface DataType {
  isFetching: boolean;
  childId?: number;
  year?: number;
  isDialogOpen: boolean;
  editingMonth: number;
  editingContent: string;
  isSaving: boolean;
}

export default defineComponent({
  components: {
    PageTitle,
    ProgressRecordEditDialog,
  },
  data: (): DataType => {
    return {
      isFetching: false,
      childId: undefined,
      year: undefined,
      isDialogOpen: false,
      editingMonth: 8,
      editingContent: '',
      isSaving: false,
    };
  },
  computed: {
    progressRecords(): ProgressRecord[] {
      const monthOrder = [8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7];
      return [...childDevelopmentChildModule.progressRecords].sort((a, b) => {
        return monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month);
      });
    },
    child() {
      return childDevelopmentChildModule.child;
    },
    childName(): string {
      if (this.child) {
        return `${this.child.lastName} ${this.child.firstName}`;
      }
      return '';
    },
    childClassId(): number | undefined {
      if (this.child && this.child.nurseryClasses && this.child.nurseryClasses.length > 0) {
        return this.child.nurseryClasses[0].classId;
      }
      return undefined;
    },
  },
  methods: {
    getMonthLabel(month: number): string {
      const monthKey = `childDevelopment.progressRecord.months.${month}`;
      const monthName = this.$t(monthKey);
      if (monthName === monthKey) {
        return `${month}`;
      }
      return monthName;
    },
    formatDate(dateString: string | null): string {
      if (!dateString) {
        return '-';
      }
      return dayjs(dateString).format('DD-MM-YYYY HH:mm');
    },
    openEditDialog(record: ProgressRecord) {
      this.editingMonth = record.month;
      this.editingContent = record.content || '';
      this.isDialogOpen = true;
    },
    async saveProgressRecord(content: string) {
      if (this.childId && this.year) {
        this.isSaving = true;
        await childDevelopmentChildModule.updateProgressRecord({
          childId: this.childId,
          businessYear: this.year,
          month: this.editingMonth,
          content: content === '' ? null : content,
        });
        this.isSaving = false;
        this.isDialogOpen = false;
      }
    },
    async listProgressRecords() {
      if (this.childId && this.year) {
        this.isFetching = true;
        await childDevelopmentChildModule.listProgressRecords({
          childId: this.childId,
          businessYear: this.year,
        });
        this.isFetching = false;
      }
    },
    async getChild() {
      if (this.childId) {
        await childDevelopmentChildModule.getChild({
          childId: this.childId,
        });
      }
    },
  },
  mounted() {
    const childId = this.$route.params.childId;
    if (childId) {
      this.childId = parseInt(childId as string, 10);
    }

    const year = this.$route.params.businessYear;
    if (year) {
      this.year = parseInt(year as string);
    }

    this.listProgressRecords();
    this.getChild();
  },
});
</script>
<style lang="scss" scoped>
.content-cell {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
